package top.wcpe.allincore.dsl.ir.model.animation

import kotlin.jvm.JvmStatic

/**
 * 弹簧物理参数 IR。
 *
 * 不可变值对象，承载单段过渡 / FLIP 位移的弹簧驱动参数：劲度系数（stiffness）、
 * 阻尼系数（damping）、质量（mass）与初速度（initialVelocity）。当 [UiAnimation]
 * 携带非空 `spring` 时，运行时改用按 dt 积分的弹簧求解器驱动过渡，取代
 * duration + easing 的进度采样。
 *
 * 默认参数对齐常见弹簧预设（react-spring 默认）：stiffness=170 / damping=26 /
 * mass=1 / initialVelocity=0，整体表现为带轻微过冲的平滑收敛。
 *
 * @throws IllegalArgumentException stiffness/mass 非正或 damping 为负时抛出
 */
class SpringSpec(
    // val 属性生成 getStiffness() / getDamping() / getMass() / getInitialVelocity()，与原 Java getter 名一致。
    /** 劲度系数，必须为正。 */
    val stiffness: Double,
    /** 阻尼系数，必须非负。 */
    val damping: Double,
    /** 质量，必须为正。 */
    val mass: Double,
    /** 初速度，可正可负。 */
    val initialVelocity: Double,
) {

    init {
        require(stiffness > 0.0) { "stiffness 必须为正" }
        require(damping >= 0.0) { "damping 必须非负" }
        require(mass > 0.0) { "mass 必须为正" }
    }

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is SpringSpec) return false
        // 复刻 Double.compare：区分 +0.0/-0.0 与 NaN 的全序比较。
        return stiffness.compareTo(other.stiffness) == 0 &&
            damping.compareTo(other.damping) == 0 &&
            mass.compareTo(other.mass) == 0 &&
            initialVelocity.compareTo(other.initialVelocity) == 0
    }

    // 逐位复刻 Objects.hash(stiffness, damping, mass, initialVelocity)：种子 1、31 倍累乘、Double.hashCode。
    override fun hashCode(): Int {
        var result = 1
        result = 31 * result + stiffness.hashCode()
        result = 31 * result + damping.hashCode()
        result = 31 * result + mass.hashCode()
        result = 31 * result + initialVelocity.hashCode()
        return result
    }

    override fun toString(): String =
        "SpringSpec{stiffness=" + stiffness +
            ",damping=" + damping +
            ",mass=" + mass +
            ",initialVelocity=" + initialVelocity + "}"

    companion object {
        // const val 复刻原 Java public static final double 编译期常量，Java 侧仍以 SpringSpec.DEFAULT_STIFFNESS 访问。
        /** 默认劲度系数：值越大弹簧越"硬"、收敛越快。 */
        const val DEFAULT_STIFFNESS: Double = 170.0

        /** 默认阻尼系数：值越大过冲越小、越快静止。 */
        const val DEFAULT_DAMPING: Double = 26.0

        /** 默认质量：值越大惯性越大、收敛越慢。 */
        const val DEFAULT_MASS: Double = 1.0

        /** 默认初速度：0 表示从静止开始。 */
        const val DEFAULT_INITIAL_VELOCITY: Double = 0.0

        /** 使用全部默认参数构造一个弹簧参数。 */
        @JvmStatic
        fun defaults(): SpringSpec =
            SpringSpec(DEFAULT_STIFFNESS, DEFAULT_DAMPING, DEFAULT_MASS, DEFAULT_INITIAL_VELOCITY)
    }
}
